import React from "react";
import { Autocomplete, Box, TextField, useTheme } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import useDebounce from "../../hooks/use-debounce";
import { AxiosResponse } from "axios";
import SearchIcon from "@mui/icons-material/Search";

interface CustomAutoCompleteProps {
  queryFn: (query: string) => Promise<AxiosResponse<any, any>>;
  onHandleSearch: (option: any) => void;
  label?: string;
  getOptionLabel?: (option: any) => string;
  selectedOptionValue?: any;
  readOnly?: boolean;
  ReadOnlyComponent?: React.ComponentType;
}

const CustomAutoComplete: React.FC<CustomAutoCompleteProps> = ({
  queryFn,
  onHandleSearch,
  label = "",
  getOptionLabel = (value: any) => String(value),
  selectedOptionValue = null,
  readOnly = false,
  ReadOnlyComponent,
}) => {
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = React.useState<string>("");
  const debounceValue = useDebounce(searchQuery, 500);
  const [selectedOption, setSelectedOption] =
    React.useState<any>(selectedOptionValue);
  const [isClearing, setIsClearing] = React.useState(false);

  const searchPatternQuery = useQuery<any>({
    queryKey: [debounceValue],
    queryFn: () => queryFn(debounceValue),
    refetchOnWindowFocus: false,
    enabled: !isClearing && debounceValue.length >= 3 && !selectedOption,
  });

  // Move useMemo to the top level
  const options = React.useMemo(() => {
    if (readOnly) return [];
    if (
      searchPatternQuery &&
      !searchPatternQuery.isLoading &&
      !searchPatternQuery.isFetching &&
      searchPatternQuery.isSuccess &&
      searchPatternQuery.data
    ) {
      return Array.isArray(searchPatternQuery.data)
        ? searchPatternQuery.data
        : [];
    }
    return [];
  }, [searchPatternQuery, readOnly]);

  if (readOnly && ReadOnlyComponent) {
    return <ReadOnlyComponent />;
  }

  const handleInputChange = (
    _event: React.SyntheticEvent,
    newInputValue: string
  ) => {
    setSearchQuery(newInputValue);
    setIsClearing(newInputValue === "");

    if (newInputValue === "") {
      setSelectedOption(null);
      onHandleSearch(null);
    }
  };

  return (
    <Box sx={{ width: "100%", position: "relative" }}>
      <Autocomplete
        value={selectedOption}
        onChange={(_event, newValue) => {
          setSelectedOption(newValue);
          onHandleSearch(newValue);
        }}
        inputValue={searchQuery}
        onInputChange={handleInputChange}
        options={options}
        filterOptions={(x) => x}
        loading={searchPatternQuery.isLoading || searchPatternQuery.isFetching}
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={(option, value) =>
          option?.uniqueKey === value?.uniqueKey
        }
        renderOption={(props, option) => {
          const { key, ...otherProps } = props;
          return (
            <Box
              key={key}
              component="li"
              {...otherProps}
              sx={{
                fontFamily: "IRANSansX",
                py: 1,
                display: "flex",
                justifyContent: "space-between",
                backgroundColor: theme.customColors.neutral.surfaceContainerLowest,
              }}
            >
              <span>{getOptionLabel(option)}</span>
            </Box>
          );
        }}
        renderInput={(params) => (
          <Box sx={{ position: "relative" }}>
            <TextField
              {...params}
              label=""
              variant="outlined"
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <SearchIcon
                    sx={{
                      width: 16,
                      height: 16,
                      color: theme.customColors.neutral[50],
                      marginRight: 1,
                    }}
                  />
                ),
                sx: {
                  fontFamily: "IRANSansX",
                  fontSize: "14px",
                  height: 48,
                  padding: "12px 12px 12px 40px",
                  borderRadius: "12px",
                  border: `1px solid ${theme.customColors.neutral[80]}`,
                  backgroundColor: theme.customColors.neutral.surfaceContainerLowest,
                  "&:hover": {
                    borderColor: theme.customColors.neutral[80],
                    backgroundColor: theme.customColors.neutral.surfaceContainerLowest,
                  },
                  "&.Mui-focused": {
                    borderColor: theme.customColors.neutral[80],
                    backgroundColor: theme.customColors.neutral.surfaceContainerLowest,
                  },
                },
              }}
              sx={{
                width: "100%",
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    border: `1px solid ${theme.customColors.neutral[80]}`,
                    borderRadius: "12px",
                  },
                  "&:hover fieldset": {
                    border: `1px solid ${theme.customColors.neutral[80]}`,
                  },
                  "&.Mui-focused fieldset": {
                    border: `1px solid ${theme.customColors.neutral[80]}`,
                  },
                },
              }}
            />
            {!searchQuery && (
              <Box
                component="span"
                sx={{
                  fontFamily: "IRANSansX",
                  fontSize: "14px",
                  color: theme.customColors.neutral[50],
                  position: "absolute",
                  left: 40,
                  top: "50%",
                  transform: "translateY(-50%)",
                  pointerEvents: "none",
                }}
              >
                {label}
              </Box>
            )}
          </Box>
        )}
        sx={{
          "& .MuiAutocomplete-popper": {
            "& .MuiPaper-root": {
              backgroundColor: theme.customColors.neutral.surfaceContainerLowest,
            },
          },
        }}
      />
    </Box>
  );
};

export default CustomAutoComplete;
